import { Browser } from '@capacitor/browser';
import { appFlags } from './appFlags';
import { ausgabeFehlerMelden, dateiAusgeben } from './dateiAusgabe';
import { hatPlugin, istNativeApp } from './nativePlatform';

/**
 * Abfang-Schicht für die Android-App (docs/android-app-vertrag.md E14, Paket F).
 *
 * Die Tablet-Variante hat den vollen Desktop-Umfang — und damit rund fünfzig
 * Stellen, die eine Datei „herunterladen", ein Fenster öffnen oder drucken.
 * Die Android-WebView kann davon nichts: Blob-Downloads verpuffen ohne
 * Meldung, es gibt keinen PDF-Betrachter, kein `window.print` und keine
 * zweiten Fenster (ein neues Fenster auf derselben Domain lädt die Seite IN
 * der App und nimmt dem Nutzer die Anwendung weg). Statt jede Stelle einzeln
 * umzubauen, biegt diese Schicht die wenigen Browser-Grundwege um:
 *
 *   a) Anker-Klicks mit `blob:`/`data:` (jsPDF `save`, file-saver `saveAs`,
 *      `a.download` + `a.click()`) → `dateiAusgeben` (Teilen-Dialog).
 *      http(s) mit `download` → abrufen und ausgeben, sonst System-Browser.
 *   b) `window.open`: http(s) → System-Browser; `blob:`/`data:` → Datei.
 *   c) `window.print()` → Native-Plugin `AppFlags.drucken()`.
 *   d) Klick auf `<a target="_blank">` → System-Browser statt Wegnavigieren.
 *
 * NUR in der App: `installiereNativeShims()` kehrt im Browser sofort zurück
 * und fasst dort nichts an — kein Objekt, kein Prototyp, kein Listener.
 * `mailto:`/`tel:` und alle Links ohne neues Fenster bleiben unberührt.
 */

/** Wie lange ein freigegebener Blob noch auffindbar bleibt (s. `merkeWiderruf`). */
const NACHLAUF_MS = 60_000;
/**
 * Obergrenze für freigegebene, aber noch gehaltene Blobs. Die Angebotsvorschau
 * erzeugt bei jeder Tipp-Pause ein neues PDF (mit Original-LV mehrere MB) —
 * 60 s Nachlauf ohne Deckel hielten davon auf dem Tablet leicht Hunderte MB.
 */
const NACHLAUF_MAX_BYTES = 32 * 1024 * 1024;

/** Kennzeichen am `window`: schon installiert (auch über eine zweite Modul-Instanz hinweg). */
const MARKE = Symbol.for('sigeko.nativeShims');

// ─── Objekt-URLs mitschneiden ────────────────────────────────────────────────

/** Blob je gültiger Objekt-URL. Hält nichts, was der Browser nicht ohnehin hält. */
const lebend = new Map<string, Blob>();
/** Freigegebene URLs, noch kurz auffindbar. Einfüge-Reihenfolge = Alter. */
const nachlauf = new Map<string, Blob>();
let nachlaufBytes = 0;

/**
 * Viele Stellen geben die Objekt-URL gleich nach `a.click()` frei
 * (`URL.revokeObjectURL` im selben Durchlauf). Den Blob holt die Schicht zwar
 * schon beim Klick ab — der Nachlauf deckt trotzdem ab, dass ein Weg die URL
 * erst danach auflöst.
 */
function merkeWiderruf(url: string): void {
  const blob = lebend.get(url);
  if (!blob) return;
  lebend.delete(url);
  if (nachlauf.has(url)) return;
  nachlauf.set(url, blob);
  nachlaufBytes += blob.size;
  setTimeout(() => vergiss(url), NACHLAUF_MS);
  // Der jüngste Eintrag bleibt immer, auch wenn er allein über der Grenze liegt.
  while (nachlaufBytes > NACHLAUF_MAX_BYTES && nachlauf.size > 1) {
    const aeltester = nachlauf.keys().next().value;
    if (aeltester === undefined) break;
    vergiss(aeltester);
  }
}

function vergiss(url: string): void {
  const blob = nachlauf.get(url);
  if (!blob) return;
  nachlauf.delete(url);
  nachlaufBytes -= blob.size;
}

/** Blob-URLs tragen kein Fragment — `…#toolbar=0` (Plan-Vorschau) trotzdem finden. */
function ohneFragment(url: string): string {
  const raute = url.indexOf('#');
  return raute >= 0 ? url.slice(0, raute) : url;
}

function bekannterBlob(url: string): Blob | null {
  const schluessel = ohneFragment(url);
  return lebend.get(schluessel) ?? nachlauf.get(schluessel) ?? null;
}

// ─── Hilfen: Adressen, Namen, data:-URLs ────────────────────────────────────

function schemaVon(url: string): string {
  const treffer = /^([a-z][a-z0-9+.-]*):/i.exec(url);
  return treffer ? `${treffer[1].toLowerCase()}:` : '';
}

/** Relative Adressen gegen die aktuelle Seite auflösen; `blob:`/`data:` bleiben wörtlich. */
function absolut(roh: string): string | null {
  if (schemaVon(roh)) return roh;
  try {
    return new URL(roh, window.location.href).href;
  } catch {
    return null;
  }
}

/** Öffnet dieses Ziel ein NEUES Fenster? (`''`, `_self`, `_top`, `_parent` = dasselbe) */
function neuesFenster(target: string | null | undefined): boolean {
  const t = String(target ?? '').trim().toLowerCase();
  return t !== '' && t !== '_self' && t !== '_top' && t !== '_parent';
}

const ENDUNGEN: Record<string, string> = {
  'application/pdf': '.pdf',
  'application/json': '.json',
  'application/xml': '.xml',
  'text/xml': '.xml',
  'application/zip': '.zip',
  'application/x-zip-compressed': '.zip',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
  'application/vnd.android.package-archive': '.apk',
  'message/rfc822': '.eml',
  'text/calendar': '.ics',
  'text/csv': '.csv',
  'text/plain': '.txt',
  'text/html': '.html',
  'image/png': '.png',
  'image/jpeg': '.jpg',
  'image/webp': '.webp',
  'image/gif': '.gif',
  'image/svg+xml': '.svg',
};

function endungFuer(typ: string): string {
  return ENDUNGEN[String(typ || '').split(';')[0].trim().toLowerCase()] ?? '';
}

/**
 * Dateiname für den Teilen-Dialog. Ohne Endung erkennt Android den Typ nicht
 * (dateiAusgabe.ts, `cacheDateiname`) — fehlt sie, kommt sie aus dem Blob-Typ.
 */
function dateinameFuer(vorschlag: string, blob: Blob): string {
  let name = String(vorschlag || '').trim();
  if (!name && typeof File !== 'undefined' && blob instanceof File) name = String(blob.name || '').trim();
  if (!name) name = 'Datei';
  const endung = endungFuer(blob.type);
  return endung && !/\.[a-z0-9]{1,8}$/i.test(name) ? `${name}${endung}` : name;
}

function sicherDekodieren(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

/** Prozent-kodierte Nutzlast → Bytes (`%C3%BC` bleibt ein ü, kein Doppel-UTF-8). */
function prozentZuBytes(s: string): Uint8Array {
  const kodierer = new TextEncoder();
  const bytes: number[] = [];
  for (let i = 0; i < s.length;) {
    if (s[i] === '%' && /^[0-9a-f]{2}$/i.test(s.slice(i + 1, i + 3))) {
      bytes.push(parseInt(s.slice(i + 1, i + 3), 16));
      i += 3;
      continue;
    }
    const zeichen = String.fromCodePoint(s.codePointAt(i) ?? 0xfffd);
    kodierer.encode(zeichen).forEach((b) => bytes.push(b));
    i += zeichen.length;
  }
  return new Uint8Array(bytes);
}

/**
 * `data:`-Adresse → Blob, ohne `fetch` (kein Netz, keine CSP-Frage).
 * `name` stammt aus einem `filename=`/`name=`-Parameter, wie ihn jsPDF
 * (`output('datauristring')`) setzt — sonst leer.
 */
export function dataUrlZuBlob(url: string): { blob: Blob; name: string } {
  const komma = url.indexOf(',');
  if (!/^data:/i.test(url) || komma < 0) throw new Error('Ungültige data:-Adresse.');
  const teile = url.slice(5, komma).split(';');
  const nutzlast = url.slice(komma + 1);
  let typ = '';
  let base64 = false;
  let name = '';
  teile.forEach((teil, i) => {
    const t = teil.trim();
    if (i === 0 && t.includes('/')) { typ = t.toLowerCase(); return; }
    if (t.toLowerCase() === 'base64') { base64 = true; return; }
    const gleich = t.indexOf('=');
    if (gleich <= 0) return;
    const schluessel = t.slice(0, gleich).trim().toLowerCase();
    if (schluessel === 'filename' || schluessel === 'name') name = sicherDekodieren(t.slice(gleich + 1).trim());
  });
  let bytes: Uint8Array;
  if (base64) {
    const binaer = atob((nutzlast.includes('%') ? sicherDekodieren(nutzlast) : nutzlast).replace(/\s+/g, ''));
    bytes = new Uint8Array(binaer.length);
    for (let i = 0; i < binaer.length; i++) bytes[i] = binaer.charCodeAt(i);
  } else {
    bytes = prozentZuBytes(nutzlast);
  }
  return { blob: new Blob([bytes as BlobPart], { type: typ || 'text/plain' }), name };
}

/** Name aus `Content-Disposition` (RFC 6266: `filename*=UTF-8''…` vor `filename=`). */
function nameAusContentDisposition(kopf: string | null): string {
  if (!kopf) return '';
  const stern = /filename\*\s*=\s*(?:UTF-8|utf-8)?'[^']*'([^;]+)/.exec(kopf);
  if (stern) return sicherDekodieren(stern[1].trim().replace(/^"|"$/g, ''));
  const einfach = /filename\s*=\s*("([^"]*)"|[^;]+)/i.exec(kopf);
  return einfach ? (einfach[2] ?? einfach[1]).trim() : '';
}

/** Name aus der Adresse: Supabase-`?download=<name>`, sonst letzter Pfadteil. */
function nameAusUrl(url: string): string {
  try {
    const u = new URL(url);
    const ausParameter = u.searchParams.get('download');
    if (ausParameter) return ausParameter;
    const letzter = u.pathname.split('/').filter(Boolean).pop() ?? '';
    return sicherDekodieren(letzter);
  } catch {
    return '';
  }
}

/**
 * Blob hinter einer Adresse: mitgeschnittene Objekt-URL, `data:` oder Abruf.
 * Auch für Aufrufer mit eigenem App-Zweig (Angebotsvorschau).
 */
export async function blobFuerUrl(url: string): Promise<Blob> {
  const bekannt = bekannterBlob(url);
  if (bekannt) return bekannt;
  if (/^data:/i.test(url)) return dataUrlZuBlob(url).blob;
  const antwort = await fetch(url);
  if (!antwort.ok) throw new Error(`Datei nicht abrufbar (HTTP ${antwort.status}).`);
  return antwort.blob();
}

// ─── Ziele ausführen ─────────────────────────────────────────────────────────

type Ziel =
  /** `blob:`/`data:` — die Datei liegt schon im Speicher. */
  | { art: 'datei'; url: string; name: string; blob: Blob | null }
  /** http(s) mit `download` — erst abrufen. */
  | { art: 'laden'; url: string; name: string }
  /** http(s) in neuem Fenster — System-Browser. */
  | { art: 'extern'; url: string };

/** Ursprüngliches `window.open` — Notweg des System-Browsers (nie der eigene Ersatz). */
let originalOpen: typeof window.open | null = null;

/**
 * Wie `externOeffnen` (Browser.open, ohne zu warten) — aber mit dem
 * URSPRÜNGLICHEN `window.open` als Notweg: `externOeffnen` fiele bei einem
 * Plugin-Fehler auf `window.open` zurück, also auf diese Schicht, und die
 * riefe wieder `externOeffnen` — eine Schleife ohne Ende.
 *
 * Ohne natives Browser-Plugin gar nicht erst `Browser.open`: dessen
 * Web-Umsetzung (greift z. B. unter `CapacitorCustomPlatform`) öffnet selbst
 * per `window.open` — also wieder hier. Im Abnahmelauf hing die Seite genau so.
 */
function imSystemBrowser(url: string): void {
  const notweg = () => {
    const oeffne = originalOpen ?? window.open;
    oeffne.call(window, url, '_blank', 'noopener,noreferrer');
  };
  if (!hatPlugin('Browser')) {
    notweg();
    return;
  }
  try {
    Browser.open({ url }).catch((e: unknown) => {
      console.error('[nativeShims] System-Browser nicht erreichbar, weiche auf window.open aus:', e);
      notweg();
    });
  } catch (e) {
    console.error('[nativeShims] System-Browser nicht erreichbar, weiche auf window.open aus:', e);
    notweg();
  }
}

async function dateiAusUrl(url: string, name: string, bekannt: Blob | null): Promise<void> {
  try {
    let blob = bekannt;
    let vorschlag = name;
    if (!blob) {
      if (/^data:/i.test(url)) {
        const zerlegt = dataUrlZuBlob(url);
        blob = zerlegt.blob;
        vorschlag = vorschlag || zerlegt.name;
      } else {
        blob = await blobFuerUrl(url);
      }
    }
    await dateiAusgeben(blob, dateinameFuer(vorschlag, blob));
  } catch (e) {
    ausgabeFehlerMelden(e);
  }
}

async function ladenUndAusgeben(url: string, name: string): Promise<void> {
  let blob: Blob;
  let vorschlag = name;
  try {
    const antwort = await fetch(url);
    if (!antwort.ok) throw new Error(`HTTP ${antwort.status}`);
    blob = await antwort.blob();
    vorschlag = vorschlag || nameAusContentDisposition(antwort.headers.get('Content-Disposition')) || nameAusUrl(url);
  } catch (e) {
    // Fremde Server ohne CORS, abgelaufene Links …: der System-Browser kann es oft trotzdem.
    console.warn('[nativeShims] Datei nicht abrufbar, öffne sie im System-Browser:', e);
    imSystemBrowser(url);
    return;
  }
  try {
    await dateiAusgeben(blob, dateinameFuer(vorschlag, blob));
  } catch (e) {
    ausgabeFehlerMelden(e);
  }
}

function zielAusfuehren(ziel: Ziel): void {
  if (ziel.art === 'datei') void dateiAusUrl(ziel.url, ziel.name, ziel.blob);
  else if (ziel.art === 'laden') void ladenUndAusgeben(ziel.url, ziel.name);
  else imSystemBrowser(ziel.url);
}

// ─── a) + d): Anker ──────────────────────────────────────────────────────────

/**
 * Urteil über einen Anker, SYNCHRON beim Klick (der Blob wird hier schon
 * abgeholt — die Aufrufer geben die Objekt-URL oft im selben Durchlauf frei).
 * `null` = nicht unsere Sache, der Klick läuft wie immer.
 */
function ankerZiel(a: HTMLAnchorElement): Ziel | null {
  const href = String(a.href || '');
  if (!href) return null;
  const schema = schemaVon(href);
  const mitDownload = a.hasAttribute('download');
  const name = mitDownload ? String(a.download || '') : '';
  if (schema === 'blob:' || schema === 'data:') {
    // Auch ohne `download`: eine blob-/data-Adresse in der WebView zu öffnen
    // hieße, die Anwendung durch die Datei zu ersetzen.
    return { art: 'datei', url: href, name, blob: bekannterBlob(href) };
  }
  if (schema !== 'http:' && schema !== 'https:') return null; // mailto:, tel:, javascript: …
  if (mitDownload) return { art: 'laden', url: href, name };
  if (neuesFenster(a.target)) return { art: 'extern', url: href };
  return null;
}

function ankerAus(ziel: EventTarget | null): HTMLAnchorElement | null {
  if (!ziel || typeof (ziel as Element).closest !== 'function') return null;
  const a = (ziel as Element).closest('a');
  return a instanceof HTMLAnchorElement ? a : null;
}

/**
 * Abbrechbare Kopie eines Klicks. Ein synthetischer Klick ohne `cancelable`
 * (jsPDF/file-saver: `new MouseEvent('click')`) lässt sich nicht abbrechen:
 * `preventDefault` wirkt nicht, und der Standardweg liefe NEBEN der nativen
 * Ausgabe her. Die Kopie trägt dieselben Angaben, ist aber abbrechbar.
 */
function abbrechbareKopie(ev: Event): Event {
  const basis = { bubbles: ev.bubbles, cancelable: true, composed: ev.composed };
  if (typeof MouseEvent !== 'undefined' && ev instanceof MouseEvent) {
    return new MouseEvent(ev.type, {
      ...basis,
      view: ev.view,
      detail: ev.detail,
      screenX: ev.screenX,
      screenY: ev.screenY,
      clientX: ev.clientX,
      clientY: ev.clientY,
      ctrlKey: ev.ctrlKey,
      shiftKey: ev.shiftKey,
      altKey: ev.altKey,
      metaKey: ev.metaKey,
      button: ev.button,
      buttons: ev.buttons,
      relatedTarget: ev.relatedTarget,
    });
  }
  return new Event(ev.type, basis);
}

/** Oberster Knoten über einem losgelösten Anker — dort kommt ein blasender Klick zuletzt an. */
function wurzelVon(knoten: Node): Node {
  let k = knoten;
  while (k.parentNode) k = k.parentNode;
  return k;
}

/** Losgelöste Anker, deren Klick gerade läuft — wie das „click in progress"-Kennzeichen von `click()`. */
const laufendeKlicks = new WeakSet<HTMLAnchorElement>();

/**
 * Klick auf einen LOSGELÖSTEN Anker zustellen (`ereignis` ist abbrechbar).
 *
 * Die eigenen Handler des Ankers (addEventListener, `onclick`) laufen wie im
 * Browser, und ihre Entscheidung zählt: Ein Hörer, der als LETZTER an die
 * Reihe kommt (am Anker bzw. — bei blasendem Klick — an der Wurzel seines
 * losgelösten Baums), liest `defaultPrevented`. Das erfasst `preventDefault()`,
 * `return false` im `onclick` und `returnValue = false` gleichermaßen.
 *   - nicht abgebrochen → der Hörer unterbindet den Standardweg, danach
 *     startet die native Ausgabe;
 *   - abgebrochen → weder Standardweg noch native Ausgabe.
 * Hält ein Handler den Klick an (`stopPropagation`/`stopImmediatePropagation`),
 * erreicht er den Hörer nicht: dann läuft allein der Standardweg. Nie beide.
 */
function losgeloestZustellen(
  a: HTMLAnchorElement,
  ereignis: Event,
  ziel: Ziel,
  zustellen: (ev: Event) => boolean,
): boolean {
  const letzter: EventTarget = ereignis.bubbles ? wurzelVon(a) : a;
  let unserer = false;
  const hoerer = (ev: Event): void => {
    if (ev !== ereignis || ev.defaultPrevented) return;
    ev.preventDefault();
    unserer = true;
  };
  letzter.addEventListener('click', hoerer);
  laufendeKlicks.add(a);
  let ergebnis: boolean;
  try {
    ergebnis = zustellen(ereignis);
  } finally {
    letzter.removeEventListener('click', hoerer);
    laufendeKlicks.delete(a);
  }
  if (unserer) zielAusfuehren(ziel);
  return ergebnis;
}

/**
 * Anker-Klicks, die der Dokument-Listener (`beiKlick`) nicht sicher lenken kann:
 *
 * - Losgelöste Anker (nicht im Dokument): jsPDF `save` und file-saver
 *   `saveAs` lösen mit `a.dispatchEvent(new MouseEvent('click'))` aus, andere
 *   mit `a.click()`. Beides erreicht den Dokument-Listener nicht — deshalb
 *   hier am Prototyp, über `losgeloestZustellen` (eigene Handler laufen).
 * - Nicht abbrechbare Klicks (`cancelable === false`), auch auf Ankern IM
 *   Dokument: `preventDefault` bliebe wirkungslos, Standardweg und native
 *   Ausgabe liefen beide. Sie werden VOR der Zustellung durch eine
 *   abbrechbare Kopie ersetzt; die läuft dann den gewohnten Weg.
 *
 * Abbrechbare Klicks auf Anker IM Dokument laufen unverändert zum Listener,
 * damit ihre eigenen Handler wie gewohnt feuern.
 */
function ankerAbfangen(): void {
  const proto = HTMLAnchorElement.prototype;
  const originalKlick = proto.click;
  const originalDispatch = proto.dispatchEvent;
  proto.click = function click(this: HTMLAnchorElement): void {
    if (!this.isConnected) {
      // Wie `click()` selbst: kein zweiter Klick, solange der erste läuft.
      if (laufendeKlicks.has(this)) return;
      const ziel = ankerZiel(this);
      if (ziel) {
        // Wie der Klick, den `click()` selbst erzeugt: blasend und abbrechbar.
        const ereignis = new MouseEvent('click', { bubbles: true, cancelable: true, composed: true, view: window });
        losgeloestZustellen(this, ereignis, ziel, (ev) => originalDispatch.call(this, ev));
        return;
      }
    }
    originalKlick.call(this);
  };
  proto.dispatchEvent = function dispatchEvent(this: HTMLAnchorElement, ev: Event): boolean {
    if (!ev || ev.type !== 'click') return originalDispatch.call(this, ev);
    const verbunden = this.isConnected;
    if (verbunden && ev.cancelable) return originalDispatch.call(this, ev); // → beiKlick
    const ziel = ankerZiel(this);
    if (!ziel) return originalDispatch.call(this, ev);
    const abbrechbar = ev.cancelable ? ev : abbrechbareKopie(ev);
    // Im Dokument: die abbrechbare Kopie läuft über beiKlick, EIN Weg.
    if (verbunden) return originalDispatch.call(this, abbrechbar);
    // Losgelöst: false = „abgebrochen" — Standardweg entfällt (oder ein Handler brach ab).
    return losgeloestZustellen(this, abbrechbar, ziel, (e) => originalDispatch.call(this, e));
  };
}

/**
 * Capture-Listener am Dokument: sieht jeden Klick zuerst, auch echte
 * Fingertipps auf gerenderte Links (`<a download>`, `<a target="_blank">`).
 *
 * Der Standardweg wird SOFORT unterbunden (die WebView darf weder
 * wegnavigieren noch vergeblich laden). Ausgeführt wird erst nach dem
 * Durchlauf — ruft ein Handler der Anwendung selbst `preventDefault()`, will
 * er den Klick selbst behandeln, und die Schicht hält sich heraus.
 *
 * FOLGE FÜR HANDLER DER ANWENDUNG (nur App, nur Anker mit Ziel — blob:/data:,
 * http(s) mit `download`, neues Fenster): Sie sehen `ev.defaultPrevented ===
 * true` (React: `e.isDefaultPrevented() === true`), weil der Standardweg schon
 * vor ihnen unterbunden ist. Ein Handler, der daran entscheidet
 * (`if (ev.defaultPrevented) return`), verhält sich in der App deshalb anders
 * als im Browser. Ihr eigenes `preventDefault()` wird trotzdem erkannt (s. u.).
 * Stand 26.09.: kein Klick-Handler im Bestand liest `defaultPrevented`.
 *
 * Nicht abbrechbare Klicks, die hier trotzdem ankommen (synthetisch auf einem
 * KIND des Ankers ausgelöst — die am Anker selbst ersetzt `ankerAbfangen`
 * vorher), lässt die Schicht in Ruhe: der Standardweg läuft ohnehin, eine
 * native Ausgabe wäre der zweite Weg.
 */
function beiKlick(ev: Event): void {
  if (ev.defaultPrevented) return;
  const a = ankerAus(ev.target);
  if (!a) return;
  const ziel = ankerZiel(a);
  if (!ziel) return;
  if (!ev.cancelable) {
    console.warn('[nativeShims] Nicht abbrechbarer Klick auf einem Link — der Standardweg bleibt.');
    return;
  }
  const standard = ev.preventDefault;
  standard.call(ev);
  let fremdVerhindert = false;
  try {
    Object.defineProperty(ev, 'preventDefault', {
      configurable: true,
      value: function preventDefault(): void {
        fremdVerhindert = true;
        standard.call(ev);
      },
    });
  } catch {
    // Nicht überschreibbar: dann gilt der Klick als unserer.
  }
  setTimeout(() => {
    if (!fremdVerhindert) zielAusfuehren(ziel);
  }, 0);
}

// ─── b): window.open ─────────────────────────────────────────────────────────

/**
 * Rückgabe für umgeleitete Fenster. `null` hieße für die Aufrufer „Popup
 * blockiert" — geöffnet wurde aber sehr wohl etwas (System-Browser bzw.
 * Teilen-Dialog), nur eben nicht als Fenster dieser Seite.
 *
 * ACHTUNG: nur ein Platzhalter mit `closed`, `opener`, `focus`, `blur`,
 * `postMessage` und `close`. Er hat KEIN `document` und KEINE `location` —
 * `w.document.write(…)` oder `w.location.href = …` werfen hier einen
 * TypeError. Aufrufer, die ihr Fenster selbst beschreiben oder umlenken
 * (Angebotsvorschau), brauchen einen eigenen App-Zweig (s. `window.open('')`).
 */
function ersatzFenster(): WindowProxy {
  const ersatz = {
    closed: false,
    opener: null,
    focus(): void { /* liegt außerhalb der App */ },
    blur(): void { /* liegt außerhalb der App */ },
    postMessage(): void { /* kein Fenster dieser Seite */ },
    close(): void { ersatz.closed = true; },
  };
  return ersatz as unknown as WindowProxy;
}

function fensterAbfangen(): void {
  const original = window.open;
  originalOpen = original;
  window.open = function open(url?: string | URL, target?: string, features?: string): WindowProxy | null {
    // Leeres Ziel heißt bei window.open „neues Fenster" (anders als am Anker).
    const ziel = target === undefined || target === '' ? '_blank' : target;
    if (!neuesFenster(ziel)) return original.call(window, url, target, features);
    const roh = url === undefined || url === null ? '' : String(url).trim();
    if (roh === '' || /^about:blank$/i.test(roh)) {
      // Selbst beschriebene Fenster (Angebotsvorschau) gibt es in der WebView
      // nicht — deren Aufrufer haben einen eigenen App-Zweig. Das Original
      // würde die App durch eine leere Seite ersetzen.
      console.warn('[nativeShims] Leeres Fenster ist in der App nicht möglich.');
      return null;
    }
    const adresse = absolut(roh);
    const schema = adresse ? schemaVon(adresse) : '';
    if (adresse && (schema === 'blob:' || schema === 'data:')) {
      void dateiAusUrl(adresse, '', bekannterBlob(adresse));
      return ersatzFenster();
    }
    if (adresse && (schema === 'http:' || schema === 'https:')) {
      imSystemBrowser(adresse);
      return ersatzFenster();
    }
    return original.call(window, url, target, features); // mailto:, tel: … wie bisher
  };
}

// ─── c): window.print ────────────────────────────────────────────────────────

// Plugin `AppFlags`: registriert erst beim ersten Zugriff, an EINER Stelle
// (appFlags.ts) für alle Aufrufer — hier `drucken`, in dateiAusgabe.ts
// `emailEntwurf`.

function druckenAbfangen(): void {
  const original = window.print;
  window.print = function print(): void {
    // Ältere APK ohne Plugin: bisheriges Verhalten.
    if (!hatPlugin('AppFlags')) { original.call(window); return; }
    const titel = typeof document !== 'undefined' ? String(document.title || '').trim() : '';
    let lauf: Promise<void>;
    try {
      lauf = appFlags().drucken(titel ? { titel } : {});
    } catch (e) {
      lauf = Promise.reject(e);
    }
    lauf.catch((e: unknown) => {
      // Auch „Methode fehlt" (APK mit AppFlags, aber vor `drucken`) landet hier.
      console.error('[nativeShims] Drucken über die App nicht möglich:', e);
      original.call(window);
    });
  };
}

// ─── Einbau ──────────────────────────────────────────────────────────────────

/**
 * Einmal beim Start aufrufen, VOR dem ersten Render (index.tsx).
 *
 * Browser: sofort `false`, nichts wird angefasst. App: installiert a)–d) und
 * liefert `true`; jeder weitere Aufruf ist wirkungslos (`false`). Wirft nie —
 * ein Teil, der scheitert, fällt einzeln aus, die Anwendung startet trotzdem.
 */
export function installiereNativeShims(): boolean {
  if (!istNativeApp()) return false;
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;
  const marken = window as unknown as Record<symbol, unknown>;
  if (marken[MARKE]) return false;
  marken[MARKE] = true;

  const teile: Array<[string, () => void]> = [
    ['Objekt-URLs', () => {
      const erzeuge = URL.createObjectURL;
      const gibFrei = URL.revokeObjectURL;
      URL.createObjectURL = function createObjectURL(obj: Blob | MediaSource): string {
        const url = erzeuge.call(URL, obj);
        if (typeof Blob !== 'undefined' && obj instanceof Blob) lebend.set(url, obj);
        return url;
      };
      URL.revokeObjectURL = function revokeObjectURL(url: string): void {
        gibFrei.call(URL, url);
        merkeWiderruf(url);
      };
    }],
    ['Anker', ankerAbfangen],
    ['window.open', fensterAbfangen],
    ['window.print', druckenAbfangen],
    ['Klick-Listener', () => document.addEventListener('click', beiKlick, true)],
  ];
  for (const [name, einbauen] of teile) {
    try {
      einbauen();
    } catch (e) {
      console.error(`[nativeShims] ${name} nicht abgefangen:`, e);
    }
  }
  return true;
}
